import EventTarget from 'goog:goog.events.EventTarget';
import {
  HTMLTemplateResult,
  RenderOptions,
  TemplateResult,
} from 'derivita/third_party/lit/packages/lit/src/html';
import { Barrier } from '../promises/barrier';
import { AsyncAnimationDelay } from 'derivita/util/animation_delay/async_animation_delay';
import { render } from './embeddable_component';

export const EventType = {
  FIRST_UPDATE: 'first-update',
  UPDATE: 'update',
};

export interface Schedule {
  startIfNotActive(): Promise<void>;
}

export interface Scheduler {
  (f: () => void): Schedule;
}

const schedulerStack: Scheduler[] = [(f) => new AsyncAnimationDelay(f)];

export class LitRenderer extends EventTarget {
  private renderer_: Schedule;
  private rendered_ = false;
  private template_?: HTMLTemplateResult;

  constructor(
    private rootElement_: HTMLElement,
    private templateGenerator_: () => HTMLTemplateResult,
    private renderingOptions_: Partial<RenderOptions> = {}
  ) {
    super();
    this.renderer_ = this.newSchedule();
  }

  /** Manually start an update. */
  requestUpdate(): Promise<void> {
    return this.renderer_.startIfNotActive();
  }

  /**
   * Immediately renders the template and triggers all associated lifecycle hooks.
   * Additionally renders any child components that have not been rendered yet.
   * @returns the root node containing the rendered component
   */
  render(): Element {
    if (this.isDisposed()) {
      return this.rootElement_;
    }
    this.template_ = this.templateGenerator_();
    render(this.template_, this.rootElement_, this.renderingOptions_);

    if (!this.rendered_) {
      this.rendered_ = true;
      this.dispatchEvent(EventType.FIRST_UPDATE);
    }

    this.dispatchEvent(EventType.UPDATE);

    return this.rootElement_;
  }

  getRenderedTemplate(): HTMLTemplateResult | undefined {
    return this.template_;
  }

  isRendered(): boolean {
    return this.rendered_;
  }

  private newSchedule() {
    const scheduler = schedulerStack[schedulerStack.length - 1];
    return scheduler(() => this.render());
  }

  static TestOnly = {
    pushInstantSchedule() {
      schedulerStack.push(InstantScheduler);
    },
    pushManualSchedule() {
      const manual = new ManualScheduler();
      schedulerStack.push(manual.scheduler);
      return manual;
    },
    popSchedule() {
      if (schedulerStack.length < 2) {
        throw new Error('Empty stack');
      }
      schedulerStack.pop();
    },
  } as const;
}

const InstantScheduler: Scheduler = function (f: () => void) {
  return {
    async startIfNotActive() {
      f();
    },
  };
};

class ManualScheduler {
  readonly scheduler: Scheduler;
  private pending = new Set<() => void>();
  private barrier = new Barrier();

  constructor() {
    this.scheduler = (f) => ({
      startIfNotActive: () => {
        this.pending.add(f);
        return this.barrier.then();
      },
    });
  }

  getPendingCount() {
    return this.pending.size;
  }

  run() {
    const toRun = this.pending;
    const done = this.barrier;

    this.pending = new Set();
    this.barrier = new Barrier();

    for (const f of toRun) {
      f();
    }
    done.unblock(null);
  }
}
